import React, { useEffect, useState } from 'react'
import styles from './index.module.css'
import { PropertyResponse, UserResponse } from '../models'
import { useLocation } from 'react-router-dom'
import Header from '../components/header'
import RoundedInput from '../components/roundedInput'
import RoundedSelect from '../components/roundedSelect'
import RoundedButton from '../components/roundedButton'
import { Modal } from '@mui/material'
import AddImageModal from '../components/addImageModal'
import ApiService from '../ApiService'
import Loading from '../components/loading'
import { Delete } from '@mui/icons-material'
import DeleteModal from '../components/deleteModal'
import { parseDate } from '../utils/parseDate'


export default function Imovel() {
    const descriptionFilterOptions = ['Entrada', 'Saída', 'Outros']
    const [openAddImageModal, setOpenAddImageModal] = useState<'image' | 'video' | undefined>()
    const [images, setImages] = useState<any>()
    const [videos, setVideos] = useState<any>()
    const [userData, setUserData] = useState<UserResponse>()
    const [isLoadingImages, setIsLoadingImages] = useState<boolean>(false)
    const [isLoadingVideos, setIsLoadingVideos] = useState<boolean>(false)
    const [openDeleteModal, setOpenDeleteModal] = useState<any>()
    const [descriptionFilter, setDescriptionFilter] = useState<string>('Todos')

    const data: {
        property: PropertyResponse,
    } = useLocation().state

    const handleGetPropertyImages = async () => {
        setIsLoadingImages(true)
        try {
            const response = await ApiService.getImageByProperty(data?.property?.id)
            setImages(response)
        }
        catch (e: any) {
            console.log(e)
        }
        finally {
            setIsLoadingImages(false)
        }
    }

    const handleGetPropertyVideos = async () => {
        setIsLoadingVideos(true)
        try {
            const response = await ApiService.getVideoByProperty(data?.property?.id)
            setVideos(response)
        }
        catch (e: any) {
            console.log(e)
        }
        finally {
            setIsLoadingVideos(false)
        }
    }

    const handleOpenVideo = (link: string) => {
        window.open(link, '_blank')
    }

    const handleDeleteMedia = async (media: { type: string, id: string }) => {
        try {
            if (media.type === 'image') {
                await ApiService.deleteImage(media.id)
            }
            if (media.type === 'video') {
                await ApiService.deleteVideo(media.id)
            }
        }
        catch (error: any) {
            console.log(error)
        }
    }

    const getUserData = async () => {
        const rawUserData = localStorage.getItem('userData')
        if (!rawUserData) {
            return
        }
        try {
            const parsedData = JSON.parse(rawUserData)
            setUserData(parsedData)
        } catch (e: any) {
            console.log(e)
        }
    }

    const refreshMedia = async () => {
        await Promise.all([
            handleGetPropertyImages(),
            handleGetPropertyVideos()
        ])
    }

    const handleCloseAddImageModal = async () => {
        setOpenAddImageModal(undefined)
        await refreshMedia()
    }

    const handleAcceptDelete = async () => {
        if (!openDeleteModal) {
            return
        }
        await handleDeleteMedia(openDeleteModal)
        await refreshMedia()
    }

    const normalizeDescription = (value?: string) => {
        return (value ?? '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
    }

    const matchesDescriptionFilter = (descriptionValue?: string) => {
        const description = normalizeDescription(descriptionValue)
        if (descriptionFilter === 'Todos') {
            return true
        }
        if (descriptionFilter === 'Entrada') {
            return description === 'entrada'
        }
        if (descriptionFilter === 'Saída') {
            return description === 'saida'
        }
        return description !== 'entrada' && description !== 'saida'
    }

    const filteredImages = images?.filter((image: any) => matchesDescriptionFilter(image?.description))
    const filteredVideos = videos?.filter((video: any) => matchesDescriptionFilter(video?.description))

    useEffect(() => {
        refreshMedia()
        getUserData()
    }, [data?.property?.id])

    return (
        <div className={styles.background}>
            <Modal open={openAddImageModal ? true : false} onClose={handleCloseAddImageModal}>
                <AddImageModal type={openAddImageModal} property={data.property} closeCallback={handleCloseAddImageModal} />
            </Modal>
            <Modal open={openDeleteModal ? true : false} onClose={() => setOpenDeleteModal(undefined)}>
                <DeleteModal resourceToDelete={openDeleteModal?.id} onCloseCallback={() => setOpenDeleteModal(undefined)} onAcceptCallback={handleAcceptDelete} />
            </Modal>
            <Header />
            <p className={styles.pageTitle}>{`Imóvel ${data.property?.code}`}</p>
            {(userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA') && <>
                <div className={styles.inputRow}>
                    <div className={styles.mediumInput}>
                        <RoundedInput label='Código do imóvel' type='number' value={data.property?.code?.toString()} onChangeCallback={(value: string) => { }} disabled />
                    </div>
                    <div className={styles.mediumInput}>
                        <RoundedInput label='Proprietário' type='text' value={data.property?.landlord_name} onChangeCallback={(value: string) => { }} disabled />
                    </div>
                    <div className={styles.mediumInput}>
                        <RoundedInput label='Inquilino' type='text' value={data.property?.tenant_name} onChangeCallback={(value: string) => { }} disabled />
                    </div>
                </div>
            </>}
                
            <div className={styles.inputRow}>
                <div className={styles.largeInput}>
                    <RoundedInput label='Endereço' type='text' value={data.property?.address?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.smallInput}>
                    <RoundedInput label='Número' type='text' value={data.property?.address_no?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Complemento' type='text' value={data.property?.address_l2?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='CEP' type='text' value={data.property?.cep?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
            </div>
            <div className={styles.inputRow}>

                <div className={styles.mediumInput}>
                    <RoundedInput label='Bairro' type='text' value={data.property?.address_neighborhood?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='Cidade' type='text' value={data.property?.city?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.smallInput}>
                    <RoundedInput label='Estado' type='text' value={data.property?.state?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
                <div className={styles.mediumInput}>
                    <RoundedInput label='País' type='text' value={data.property?.country?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
            </div>
            <div className={styles.inputRow}>
                <div className={styles.tallInput}>
                    <RoundedInput label='Observações' type='text' value={data.property?.details?.toString()} onChangeCallback={(value: string) => { }} disabled />
                </div>
            </div>
            {(userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA') && <>
                <div className={styles.buttonsRow}>
                    <div className={styles.uploadImageButton} onClick={() => setOpenAddImageModal('image')}>
                        <RoundedButton buttonText='Adicionar Foto' buttonType='button' />
                    </div>
                    <div className={styles.uploadVideoButton} onClick={() => setOpenAddImageModal('video')}>
                        <RoundedButton buttonText='Adicionar Vídeo' buttonType='button' />
                    </div>
                </div>
            </>}
            <div className={styles.FilterSelect}>
                <p> Filtrar por tipo </p>
                <RoundedSelect label='Todos' value={descriptionFilter} items={descriptionFilterOptions} onChange={(value: string) => setDescriptionFilter(value)}/>
            </div>
            <p className={styles.pageTitle}>Fotos</p>
            {isLoadingImages ?
                <Loading />
                :
                <div className={styles.grid}>
                    {filteredImages?.map((image: any) =>
                        <div className={styles.imageContainer}>
                            <img className={styles.image} src={image.link} />
                            <div className={styles.imageDescription}>
                                <p>{`Descrição: ${image.description ? image.description : ''}`}</p>
                                <p>{`Data da foto: ${image.date_taken ? parseDate(image.date_taken, false, false) : ''}`}</p>
                                <p>{`Adicionado em: ${parseDate(image.date_added, true, true)}`}</p>
                            </div>
                            {(userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA') &&
                                <div className={styles.redIcons} onClick={() => setOpenDeleteModal({ 'type': 'image', ...image })}>
                                    <Delete />
                                </div>
                            }
                        </div>
                    )}
                </div>
            }
            <p className={styles.pageTitle}>Vídeos</p>
            {isLoadingVideos ?
                <Loading />
                :
                <div className={styles.grid}>
                    {filteredVideos?.map((image: any) =>
                        <div className={styles.imageContainer}>
                            {image?.link?.includes('https://') ?
                                <iframe className={styles.image} frameBorder={0} src={image?.link} allowFullScreen />
                                :
                                <div>
                                    <iframe className={styles.image} frameBorder={0} src={`https://drive.google.com/file/d/${image?.link}/preview`} allow="autoplay" />
                                    <p className={styles.openVideo} onClick={() => handleOpenVideo(`https://drive.google.com/file/d/${image?.link}`)}>Ver em tela cheia</p>
                                </div>
                            }
                            <div className={styles.imageDescription}>
                                <p>{`Descrição: ${image.description ? image.description : ''}`}</p>
                                <p>{`Data do vídeo: ${image.date_taken ? parseDate(image.date_taken, true, true) : ''}`}</p>
                                <p>{`Adicionado em: ${parseDate(image.date_added, false, false)}`}</p>
                            </div>
                            {(userData?.account_type === 'admin' || userData?.account_type === 'IMOBILIARIA') &&
                                <div className={styles.redIcons} onClick={() => setOpenDeleteModal({ 'type': 'video', ...image })}>
                                    <Delete />
                                </div>
                            }
                        </div>
                    )}
                </div>
            }
        </div>
    )
}
